import { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAddPost } from '../api/useAddPost';
import { CommunityId } from '../model/community.types';
import { MAX_POST_CHARS } from '../model/newPost.constants';
import { analytics } from './analytics';
import { containsCommunityMention } from './channelMention';
import { DraftVideo, isAudioFile, useMediaDraft } from './useMediaDraft';

export { isAudioFile };
export type { DraftVideo };

type NewPost = {
  text: string;
  setText: (text: string) => void;
  files: File[];
  videos: DraftVideo[];
  mediaCount: number;
  addFiles: (files: File[]) => void;
  removeFile: (file: File) => void;
  addVideo: (uuid: string, preview?: Omit<DraftVideo, 'uuid'>) => void;
  removeVideo: (uuid: string) => void;
  canSubmit: boolean;
  isSubmitting: boolean;
  submit: () => Promise<void>;
};

export const useNewPost = (
  communityId: CommunityId,
  influencerId?: string,
): NewPost => {
  const addPost = useAddPost(communityId, influencerId);
  const [text, setText] = useState('');
  const media = useMediaDraft();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { files, videos, mediaCount, clear } = media;

  const canSubmit = (text.trim().length > 0 || mediaCount > 0) && !isSubmitting;

  const submit = useCallback(async () => {
    if (text.length > MAX_POST_CHARS) {
      toast.error("Posts can't be longer than 4K characters.");
      return;
    }

    setIsSubmitting(true);
    // Stored verbatim on purpose: escaping here double-encodes "&" into "&amp;"
    // for every plain-text reader (mobile). Escaping belongs to mdToHTML at render.
    const result = await addPost({
      text,
      files,
      videoUuids: videos.map((video) => video.uuid),
    });
    if (result.type === 'success') {
      analytics.trackPostCreated(files, videos.length);
      if (containsCommunityMention(text)) {
        analytics.trackBroadcastSent();
      }
      toast.success('Post added');
      setText('');
      clear();
    } else {
      analytics.trackPostFailed(files, videos.length, result.reason);
    }
    setIsSubmitting(false);
  }, [addPost, files, videos, text, clear]);

  return {
    text,
    setText,
    files,
    videos,
    mediaCount,
    addFiles: media.addFiles,
    removeFile: media.removeFile,
    addVideo: media.addVideo,
    removeVideo: media.removeVideo,
    canSubmit,
    isSubmitting,
    submit,
  };
};
